import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import PostCard from '../components/PostCard';
import { useAuth } from '../context/AuthContext';
import { getLocalTodayTopic } from '../data/topics';
import { findHashtags, normalizeHashtag } from '../utils/hashtags';
import { IconCompose, IconClose, IconMic, IconImage } from '../components/talkAppleIcons';
import { PersonAvatar, PeopleEmpty, PeopleHeader, PeopleLoading } from '../components/PeopleUI';
import './timeline.css';
import StoryViewer from '../components/StoryViewer';

export default function Timeline() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTag = searchParams.get('tag') || '';
  const [tagQuery, setTagQuery] = useState(selectedTag ? `#${selectedTag}` : '');
  const [tagError, setTagError] = useState('');
  const [posts, setPosts]             = useState([]);
  const [content, setContent]         = useState('');
  const [posting, setPosting]         = useState(false);
  const [postError, setPostError] = useState('');
  const [postNotice, setPostNotice] = useState('');
  const [showForm, setShowForm]       = useState(false);
  const [recording, setRecording]     = useState(false);
  const [audioBlob, setAudioBlob]     = useState(null);
  const [topic, setTopic]             = useState(null);
  const [topicFilter, setTopicFilter] = useState(false);
  const [postTopicId, setPostTopicId] = useState(0);
  const [feedFilter, setFeedFilter] = useState('all'); // 'all' | 'following'
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [imageFile, setImageFile]     = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [story, setStory] = useState(null);
  const [loadError, setLoadError] = useState('');
  const fetchVersion = useRef(0);
  const mediaRef    = useRef(null);
  const chunksRef   = useRef([]);
  const textareaRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    if (!imageFile) {
      setImagePreview('');
      return;
    }
    const u = URL.createObjectURL(imageFile);
    setImagePreview(u);
    return () => URL.revokeObjectURL(u);
  }, [imageFile]);

  const PAGE_SIZE = 20;

  const fetchPosts = async (filter = feedFilter, offset = 0, append = false) => {
    const version = ++fetchVersion.current;
    setLoadingPosts(true); setLoadError('');
    try {
      const params = new URLSearchParams();
      if (filter === 'following') params.set('following', '1');
      if (selectedTag) params.set('tag', selectedTag);
      params.set('limit', PAGE_SIZE);
      params.set('offset', offset);
      const { data } = await axios.get(`/api/posts?${params.toString()}`);
      if (version !== fetchVersion.current) return;
      if (append) {
        setPosts(prev => [...prev, ...data]);
      } else {
        setPosts(data);
      }
      setHasMore(data.length === PAGE_SIZE);
    } catch {
      if (version === fetchVersion.current) setLoadError('投稿を読み込めませんでした。');
    } finally {
      if (version === fetchVersion.current) setLoadingPosts(false);
    }
  };

  const loadMore = () => {
    if (!loadingPosts && hasMore) fetchPosts(feedFilter, posts.length, true);
  };

  useEffect(() => {
    setHasMore(true); setPosts([]);
    fetchPosts(feedFilter, 0, false);
    return () => { fetchVersion.current += 1; };
  }, [feedFilter, selectedTag]);

  useEffect(() => {
    setTagQuery(selectedTag ? `#${selectedTag}` : '');
    setTagError(''); setTopicFilter(false);
  }, [selectedTag]);

  const chooseTag = value => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('tag', value); else next.delete('tag');
    setSearchParams(next);
  };

  const searchTag = event => {
    event.preventDefault();
    const tag = normalizeHashtag(tagQuery);
    if (tagQuery.trim() && !tag) {
      setTagError('タグは64文字以内の文字・数字・アンダースコアで入力してください。空白は使えません。');
      return;
    }
    setTagError(''); chooseTag(tag);
  };

  const writeWithTag = () => {
    if (posting) return;
    setPostTopicId(0); setPostError(''); setPostNotice('');
    if (!findHashtags(content).some(match => match.tag === normalizeHashtag(selectedTag))) {
      setContent(previous => previous ? `${previous}\n\n#${selectedTag} ` : `#${selectedTag} `);
    }
    setShowForm(true);
  };

  const insertHashtag = () => {
    const input = textareaRef.current;
    const start = input?.selectionStart ?? content.length;
    const end = input?.selectionEnd ?? start;
    const marker = start > 0 && !/\s/.test(content[start - 1]) ? ' #' : '#';
    setContent(content.slice(0, start) + marker + content.slice(end));
    requestAnimationFrame(() => {
      input?.focus(); input?.setSelectionRange(start + marker.length, start + marker.length);
    });
  };

  useEffect(() => {
    axios.get('/api/topics/today')
      .then(r => setTopic(r.data))
      .catch(() => setTopic(getLocalTodayTopic()));
  }, []);

  useEffect(() => {
    if (searchParams.get('compose') !== '1') return;
    setShowForm(true);
    setPostTopicId(0);
    const next = new URLSearchParams(searchParams);
    next.delete('compose');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (showForm) textareaRef.current?.focus();
  }, [showForm]);

  const openFormFree = () => {
    if (posting) return;
    setPostError(''); setPostNotice('');
    setPostTopicId(0);
    setShowForm(v => !v);
  };

  const startRecord = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mr = new MediaRecorder(stream);
    chunksRef.current = [];
    mr.ondataavailable = e => chunksRef.current.push(e.data);
    mr.onstop = () => {
      setAudioBlob(new Blob(chunksRef.current, { type: 'audio/webm' }));
      stream.getTracks().forEach(t => t.stop());
    };
    mediaRef.current = mr;
    mr.start();
    setRecording(true);
  };
  const stopRecord = () => { mediaRef.current?.stop(); setRecording(false); };

  const submit = async () => {
    const trimmed = content.trim();
    if (posting || (!trimmed && !audioBlob && !imageFile)) return;
    setPosting(true); setPostError(''); setPostNotice('');

    const tempId = `opt-${Date.now()}`;
    const optimistic = {
      id: tempId,
      user_id: user.id, content: trimmed, language: 'ja',
      audio_url: audioBlob ? URL.createObjectURL(audioBlob) : '',
      image_url: imageFile ? URL.createObjectURL(imageFile) : '',
      created_at: new Date().toISOString(),
      username: user.username, avatar: user.avatar || '',
      native_lang: user.native_lang, learning_lang: user.learning_lang,
      like_count: 0, correction_count: 0, comment_count: 0, user_liked: null,
      topic_id: postTopicId,
      _optimistic: true,
    };

    // 即座にUIに反映してフォームを閉じる
    const belongsToFeed = feedFilter !== 'following' && (!selectedTag || findHashtags(trimmed).some(match => match.tag === normalizeHashtag(selectedTag)));
    setPosts(p => belongsToFeed ? [optimistic, ...p] : p);
    setContent(''); setAudioBlob(null); setImageFile(null); setShowForm(false);
    setPostTopicId(0);

    const fd = new FormData();
    fd.append('content', trimmed);
    fd.append('language', 'ja');
    fd.append('topic_id', postTopicId);
    if (audioBlob) fd.append('audio', audioBlob, 'voice.webm');
    if (imageFile) fd.append('image', imageFile);

    try {
      const { data } = await axios.post('/api/posts', fd);
      // 楽観的投稿をサーバーデータで置換
      setPosts(p => p.map(post => post.id === tempId ? data : post));
      setPostNotice(belongsToFeed ? '投稿しました' : selectedTag ? '投稿しました。タグを解除すると確認できます。' : '投稿しました。「すべて」で確認できます。');
    } catch {
      // Failed requests restore the draft so the user can retry without rewriting.
      setPosts(p => p.filter(post => post.id !== tempId));
      setContent(content); setAudioBlob(audioBlob); setImageFile(imageFile);
      setPostTopicId(postTopicId); setShowForm(true);
      setPostError('投稿できませんでした。内容は残っています。もう一度お試しください。');
    } finally {
      setPosting(false);
      if (optimistic.audio_url) URL.revokeObjectURL(optimistic.audio_url);
      if (optimistic.image_url) URL.revokeObjectURL(optimistic.image_url);
    }
  };

  const onLike = (postId, liked) =>
    setPosts(ps => ps.map(p => p.id === postId
      ? { ...p, like_count: p.like_count + (liked ? 1 : -1), user_liked: liked ? '1' : null }
      : p));

  const visiblePosts = topicFilter && topic
    ? posts.filter(p => Number(p.topic_id) === topic.id)
    : posts;

  const changeFeed = value => { setFeedFilter(value); setTopicFilter(false); };

  return (
    <main className="jt-screen jt-timeline" style={S.page}>
      <PeopleHeader title="ひろば">
        <button data-control="icon" data-control-id="Timeline-jt-icon-link" className="jt-icon-link jt-home-compose-icon" disabled={posting} onClick={openFormFree} aria-label="投稿を書く" aria-expanded={showForm} aria-controls="home-post-form"><IconCompose size={22} /></button>
      </PeopleHeader>

      <div className="jt-home-filters">
        <div className="jt-tabs" aria-label="投稿一覧の切り替え">
          <button data-control="tab" data-control-id="Timeline-tab" aria-pressed={feedFilter === 'all'} onClick={() => changeFeed('all')}>すべて</button>
          <button data-control="tab" data-control-id="Timeline-tab" aria-pressed={feedFilter === 'following'} onClick={() => changeFeed('following')}>フォロー中</button>
        </div>
      </div>
      <form className="jt-tag-search" onSubmit={searchTag} role="search" aria-label="ハッシュタグ検索">
        <input type="search" aria-label="ハッシュタグで探す" placeholder="ハッシュタグで探す"
          value={tagQuery} onChange={event => { setTagQuery(event.target.value); setTagError(''); }}
          aria-invalid={!!tagError} aria-describedby={tagError ? 'tag-search-error' : undefined}
          autoCapitalize="none" autoCorrect="off" enterKeyHint="search" maxLength={129} />
        <button data-control="secondary" data-control-id="Timeline-searchTag" type="submit">検索</button>
      </form>
      {tagError && <p id="tag-search-error" className="jt-feedback" role="alert">{tagError}</p>}
      {selectedTag && <div className="jt-tag-context">
        <span>#{selectedTag}</span>
        <button data-control="quiet" data-control-id="Timeline-clearTag" type="button" onClick={() => chooseTag('')}>タグを解除</button>
        <button data-control="secondary" data-control-id="Timeline-writeWithTag" type="button" onClick={writeWithTag} disabled={posting}>このタグで投稿</button>
      </div>}

      {!showForm && <button data-control="row" data-control-id="Timeline-jt-home-compose" className="jt-home-compose" onClick={openFormFree} disabled={posting} aria-label="ひとこと書く" aria-expanded={false} aria-controls="home-post-form">
        <PersonAvatar person={user || {}} size={38} />
        <span className="jt-plaza-compose-copy"><span className="jt-plaza-compose-title">{posting ? '投稿しています…' : '今日、どんなことがあった？'}</span><span className="jt-plaza-compose-hint">ひとことでも、写真だけでも。</span></span>
        <IconCompose size={19} />
      </button>}

      {postNotice && <p className="jt-plaza-notice" role="status">{postNotice}</p>}
      {postError && <p className="jt-feedback jt-plaza-post-error" role="alert">{postError}</p>}

      {showForm && (
        <section id="home-post-form" style={S.formSheet} aria-label="投稿を書く">
          {postTopicId > 0 && topic && (
            <div style={S.formTopicTag}>
              <span style={S.formTopicText}>{topic.ja}</span>
              <button data-control="icon" data-control-id="Timeline-formTopicX" style={S.formTopicX} aria-label="話題を外す" onClick={() => setPostTopicId(0)}><IconClose size={18} /></button>
            </div>
          )}

          <div style={S.formRow}>
            <PersonAvatar person={user || {}} size={38} />
            <textarea
              ref={textareaRef}
              aria-label="投稿内容"
              className="jt-plaza-textarea"
              style={S.formTextarea}
              placeholder={
                postTopicId > 0 && topic
                  ? topic.hint
                  : '好きなこと、ふと思ったこと。#タグ も自由に。'
              }
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={3}
            />
          </div>
          <p className="jt-plaza-audience">投稿はほかのユーザーにも公開されます。</p>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
            style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              if (f.size > 12 * 1024 * 1024) {
                window.alert('画像は12MB以下にしてください');
                return;
              }
              setImageFile(f);
            }}
          />
          {imagePreview && (
            <div style={S.formImagePreviewWrap}>
              <img src={imagePreview} alt="" style={S.formImagePreview} />
              <button data-control="icon" data-control-id="Timeline-formImageRemove" type="button" style={S.formImageRemove} aria-label="添付を削除" onClick={() => setImageFile(null)}><IconClose size={18} /></button>
            </div>
          )}
          <div style={S.formActions}>
            <button data-control="icon" data-control-id="Timeline-addHashtag" type="button" style={S.formImageBtn} onClick={insertHashtag} aria-label="ハッシュタグを追加">#</button>
            <button data-control="icon" data-control-id="Timeline-formImageBtn"
              type="button"
              style={S.formImageBtn}
              onClick={() => imageInputRef.current?.click()}
              title="画像を添付"
              aria-label="画像を添付"
            >
              <IconImage size={21} />
            </button>
            <button data-control="icon" data-control-id="Timeline-formRecordBtn" aria-pressed={recording}
              style={{ ...S.formRecordBtn, ...(recording ? S.formRecordBtnOn : {}) }}
              aria-label="押している間、音声を録音"
              onMouseDown={startRecord} onMouseUp={stopRecord}
              onTouchStart={startRecord} onTouchEnd={stopRecord}
            >
              <IconMic size={21} />
            </button>
            {audioBlob && <span style={S.formAudioBadge}>録音済み</span>}
            <button data-control="quiet" data-control-id="Timeline-formCancelBtn"
              style={S.formCancelBtn}
              onClick={() => {
                setShowForm(false); setPostTopicId(0);
                setContent(''); setAudioBlob(null); setImageFile(null);
              }}
            >
              キャンセル
            </button>
            <button data-control="primary" data-control-id="Timeline-formSubmitBtn"
              style={{
                ...S.formSubmitBtn,
                ...(!content.trim() && !audioBlob && !imageFile ? S.formSubmitDisabled : {}),
              }}
              onClick={submit}
              disabled={posting || (!content.trim() && !audioBlob && !imageFile)}
            >
              {posting ? '投稿中…' : '投稿する'}
            </button>
          </div>
        </section>
      )}

      <section className="jt-home-feed-heading" aria-label="表示中の投稿">
        <h2>{topicFilter ? '今日の話題の投稿' : feedFilter === 'following' ? 'フォロー中の投稿' : selectedTag ? `#${selectedTag} の投稿` : 'みんなの投稿'}</h2>
        {visiblePosts.length > 0 && <button data-control="quiet" data-control-id="Timeline-jt-home-text-button" className="jt-home-text-button" onClick={() => setStory({ posts: [...visiblePosts], title: topicFilter ? '今日の話題' : selectedTag ? `#${selectedTag}` : 'ひろば' })}>まとめて読む</button>}
      </section>
      {loadError ? <PeopleEmpty title={loadError} description="接続を確認して、もう一度お試しください。" onRetry={() => fetchPosts(feedFilter, posts.length, posts.length > 0)} /> : loadingPosts && posts.length === 0 ? <PeopleLoading /> : visiblePosts.length === 0 ? <PeopleEmpty title={topicFilter ? 'この話題の投稿はまだありません' : selectedTag ? 'このタグの投稿が見つかりません' : 'まだ投稿がありません'} description={topicFilter ? '短いひとことでも、この話題で投稿できます。' : feedFilter === 'following' ? 'フォローした人の投稿がここに表示されます。' : selectedTag ? '別のタグで探すか、このタグで投稿できます。' : '今日のできごとや写真を、ひろばに投稿してみましょう。'} /> : null}

      {visiblePosts.map(post => (
        <PostCard
          key={post.id}
          post={post}
          showTalkButton={false}
          commentsAsSheet
          feedLayout
          onLike={onLike}
          todayTopicId={topic?.id}
          onHidden={id => setPosts(ps => ps.filter(p => p.id !== id))}
        />
      ))}

      {hasMore && !loadingPosts && !loadError && posts.length > 0 && (
        <button data-control="secondary" data-control-id="Timeline-loadMoreBtn" onClick={loadMore} style={S.loadMoreBtn}>もっと見る</button>
      )}
      {loadingPosts && posts.length > 0 && (
        <p style={S.loadingMore}>読み込み中…</p>
      )}

      {story && (
        <StoryViewer
          posts={story.posts}
          title={story.title}
          onClose={() => setStory(null)}
        />
      )}

    </main>
  );
}

const button = { minHeight: 44, borderRadius: 6, padding: '10px 14px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' };
const iconButton = { ...button, width: 44, padding: 0, display: 'grid', placeItems: 'center', border: 0, flexShrink: 0 };
const S = {
  page: { background: 'var(--bg)' },
  loadMoreBtn: { ...button, display: 'block', margin: '20px auto 28px', padding: '10px 24px' },
  loadingMore: { textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, padding: '12px 0' },
  formSheet: { margin: '16px', padding: '20px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10 },
  formTopicTag: { display: 'flex', gap: 12, alignItems: 'center', padding: '0 0 12px', marginBottom: 16, borderBottom: '1px solid var(--border-light)' },
  formTopicText: { flex: 1, fontSize: 13, color: 'var(--text)', lineHeight: 1.7 },
  formTopicX: iconButton,
  formRow: { display: 'flex', gap: 12, marginBottom: 10 },
  formTextarea: { flex: 1, minWidth: 0, minHeight: 110, padding: '10px 0', borderRadius: 0, fontSize: 15, fontFamily: 'inherit', resize: 'vertical', color: 'var(--text)', lineHeight: 1.8, background: 'var(--surface)' },
  formImagePreviewWrap: { margin: '0 0 12px 50px', position: 'relative', maxWidth: 240, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' },
  formImagePreview: { width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' },
  formImageRemove: { ...iconButton, position: 'absolute', top: 4, right: 4, background: 'var(--surface)' },
  formActions: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 },
  formImageBtn: iconButton,
  formRecordBtn: iconButton,
  formRecordBtnOn: { background: 'var(--error-light)', color: 'var(--error)' },
  formAudioBadge: { fontSize: 12, color: 'var(--text-secondary)' },
  formCancelBtn: { ...button, marginLeft: 'auto', border: 0, color: 'var(--text-secondary)' },
  formSubmitBtn: { ...button, padding: '10px 20px', background: 'var(--ink-accent)', color: 'var(--plaza-on-accent)', borderColor: 'transparent' },
  formSubmitDisabled: { opacity: .4, cursor: 'not-allowed' },
};
